import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../utils/api";
import { trackOrderUpdate } from "../../utils/orderActions";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import {
  OrderDialogShell,
  OrderSummaryDetails,
  HeaderBadge,
  computeOrderTotals,
  dialogBtn,
  dialogSelect,
} from "./OrderDialog";

// One-tap accept/reject for a newly arrived online order, shown on any admin
// page, with the same details as the Orders page popup (OrderPopup).
// Orders without an address can't be accepted here (the Orders page requires
// one), so for those "Add address" opens the full order popup.
const NewOrderAlert = ({ order, pendingCount, onDone, onOpenFull }) => {
  const { locale, gstFraction, taxLabel } = useRestaurantConfig();
  const [isRejecting, setIsRejecting] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Reset when the next order in the queue is shown
  useEffect(() => {
    setIsRejecting(false);
    setReason("");
  }, [order?.orderId]);

  useEffect(() => {
    if (!isRejecting || reasons.length) return;
    api
      .get("/settings/reject-reasons")
      .then((res) => setReasons(Array.isArray(res.data?.reasons) ? res.data.reasons : []))
      .catch(() => setReasons([]));
  }, [isRejecting, reasons.length]);

  if (!order) return null;

  const rows = Array.isArray(order.products)
    ? order.products.filter((p) => p.active !== false)
    : [];
  const totals = computeOrderTotals(order, rows, gstFraction);
  const hasAddress = !!(order.address && String(order.address).trim());

  const updateStatus = async (status, rejectReason) => {
    setIsSaving(true);
    try {
      const payload = { status, address: order.address || null };
      if (status === "reject") payload.rejectReason = rejectReason;
      await trackOrderUpdate(order.orderId, () =>
        api.put(`/orders/updateStatus/${order.orderId}`, payload),
      );
      toast.success(status === "accept" ? "Order accepted" : "Order rejected");
      onDone(order.orderId);
    } catch (err) {
      if (err?.response?.status === 409) {
        // Already handled on another screen — close this popup
        toast.warning(err.response.data?.message || "Order was already updated.");
        onDone(order.orderId);
      } else {
        toast.error(err?.response?.data?.message || "Failed to update order status.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const footer = isRejecting ? (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <select
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className={`${dialogSelect} sm:flex-1 min-w-0`}
        autoFocus
      >
        <option value="">Reason for rejection</option>
        {reasons.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setIsRejecting(false)}
          disabled={isSaving}
          className={`${dialogBtn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => updateStatus("reject", reason)}
          disabled={!reason || isSaving}
          className={`${dialogBtn} bg-red-600 hover:bg-red-700 text-white`}
        >
          {isSaving ? "Rejecting…" : "Confirm reject"}
        </button>
      </div>
    </div>
  ) : (
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={() => onOpenFull(order.orderId)}
        className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
      >
        View full order
      </button>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setIsRejecting(true)}
          disabled={isSaving}
          className={`${dialogBtn} border border-gray-200 text-red-600 hover:bg-red-50 hover:border-red-200`}
        >
          Reject
        </button>
        {hasAddress ? (
          <button
            type="button"
            onClick={() => updateStatus("accept")}
            disabled={isSaving}
            className={`${dialogBtn} bg-green-600 hover:bg-green-700 text-white`}
          >
            {isSaving ? "Accepting…" : "Accept"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onOpenFull(order.orderId)}
            className={`${dialogBtn} bg-blue-600 hover:bg-blue-700 text-white`}
          >
            Add address
          </button>
        )}
      </div>
    </div>
  );

  return (
    <OrderDialogShell
      labelId="new-order-title"
      status={order.status || "pending"}
      title="New online order"
      subtitle={`Order #${order.orderId}`}
      icon="🛎️"
      pulse
      badge={pendingCount > 0 && <HeaderBadge>+{pendingCount} waiting</HeaderBadge>}
      onClose={() => onDone(order.orderId)}
      closeLabel="Decide later"
      zIndex={10000}
      footer={footer}
    >
      <OrderSummaryDetails
        order={order}
        rows={rows}
        totals={totals}
        currencySymbol={getCurrencySymbol(locale)}
        taxLabel={taxLabel}
      />
    </OrderDialogShell>
  );
};

export default NewOrderAlert;
